'use client';
import {
  Favourite,
  FavouriteFilled,
  PokerGameListChooseSeat,
  PokerGameListRestrictTableMono,
  PokerGameTypeEarlyBird,
  PokerGameTypeKo,
  PokerGameTypeZoom,
  PokerSeats10,
  PokerSeats2,
  PokerSeats3,
  PokerSeats4,
  PokerSeats5,
  PokerSeats6,
  PokerSeats7,
  PokerSeats8,
  PokerSeats9,
  PokerSpeedHyperTurbo,
  PokerSpeedRegular,
  PokerSpeedSlow,
  PokerSpeedTurbo
} from '@ps/web-fusion-icons';
import classNames from 'clsx';

import { Icon, IconButton, Tooltip } from '../../..';

import type { IconTypeList } from './CashGamesEvent';
import styles from './IconContainer.module.scss';

interface IconContainerProps {
  highlightedRow: boolean;
  gameIcons?: IconTypeList;
  onFavouritePress?: () => void;
}

const tooltipIcons = new Map<React.FC<React.SVGProps<SVGElement>>, string>([
  [Favourite, 'Favourite Game. Save this game in your Favourites lobby'],
  [
    FavouriteFilled,
    'Favourite Game. This game is currently saved as a favourite. Click to remove this game from your Favourites lobby'
  ],
  [PokerSeats2, 'Maximum 2 players per table'],
  [PokerSeats3, 'Maximum 3 players per table'],
  [PokerSeats4, 'Maximum 4 players per table'],
  [PokerSeats5, 'Maximum 5 players per table'],
  [PokerSeats6, 'Maximum 6 players per table'],
  [PokerSeats7, 'Maximum 7 players per table'],
  [PokerSeats8, 'Maximum 8 players per table'],
  [PokerSeats9, 'Maximum 9 players per table'],
  [PokerGameTypeZoom, 'Fast fold and immediately be dealt a new hand on a new table'],
  [PokerGameListChooseSeat, 'Choose your table and seat'],
  [
    PokerGameListRestrictTableMono,
    'Restricted table. Contact us at gameintegrity@pokerstars.uk for access'
  ],
  [
    PokerGameTypeEarlyBird,
    'Join now and earn 2 times more reward points for at least 10 minutes of continuous play'
  ]
]);

const colorClassForIcon = (icon: React.FC<React.SVGProps<SVGElement>>, highlighted: boolean) =>
  classNames(styles.iconColor, {
    [styles.highlightedRow]: highlighted,
    [styles.iconColorRed]:
      icon === PokerSpeedHyperTurbo || icon === PokerGameTypeKo || icon === PokerSeats2,
    [styles.iconColorBlue]: icon === PokerSeats4 || icon === PokerSpeedSlow,
    [styles.iconColorLightBlue]: icon === PokerSeats8,
    [styles.iconColorTeal]: icon === PokerSeats7 || icon === FavouriteFilled,
    [styles.iconColorBrandRed]: icon === PokerGameTypeZoom,
    [styles.iconColorGreen]:
      icon === PokerSpeedRegular || icon === PokerSeats6 || icon === PokerGameTypeEarlyBird,
    [styles.iconColorOrange]: icon === PokerSpeedTurbo || icon === PokerSeats5,
    [styles.iconColorGrey]: icon === PokerSeats9,
    [styles.iconColorMagenta]: icon === PokerSeats3,
    [styles.iconColorPurple]: icon === PokerSeats10,
    [styles.iconRestrictTable]: icon === PokerGameListRestrictTableMono
  });

const IconContainer = ({ gameIcons, highlightedRow, onFavouritePress }: IconContainerProps) => (
  <div className={styles.iconContainer}>
    {gameIcons?.map((icon) => {
      const commonClass = colorClassForIcon(icon, highlightedRow);

      if (tooltipIcons.has(icon)) {
        return (
          <Tooltip key={icon.name}>
            {icon === FavouriteFilled || icon === Favourite ? (
              <IconButton
                size="sm"
                icon={icon}
                aria-label="Favourite"
                className={commonClass}
                onPress={onFavouritePress}
              />
            ) : (
              <span className={styles.iconContainer}>
                <Icon key={icon.name} size="sm" icon={icon} className={commonClass} />
              </span>
            )}
            <span>{tooltipIcons.get(icon)}</span>
          </Tooltip>
        );
      }

      return <Icon key={icon.name} size="sm" icon={icon} className={commonClass} tabIndex={-1} />;
    })}
  </div>
);

export default IconContainer;
